import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Loader } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import {
  libraryBackLabel,
  libraryBackUrl,
  libraryEditUrl,
  libraryShowUrl,
} from '@/lib/library-navigation';

interface LibraryDetail {
  id: string;
  title: string;
  slug: string;
  type: string;
  status?: string;
  currentVersion?: { content?: unknown; versionNumber?: number };
}

export function LibraryPreviewPage() {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.BIBLIOTECA_EDITAR);
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const detailUrl = id ? libraryShowUrl(id, searchParams) : '/library';
  const backUrl = id ? detailUrl : libraryBackUrl(searchParams);
  const backLabel = id ? 'Volver al detalle del objeto' : libraryBackLabel(searchParams);
  const editUrl = id ? libraryEditUrl(id, searchParams) : '/library';
  const { data, isLoading, error } = useQuery({
    queryKey: ['library', id],
    queryFn: () => api<LibraryDetail>(`/library/${id}`),
    enabled: Boolean(id),
  });

  return (
    <div className="lms-library-preview lms-site-admin-hub-page font-sans">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <HubBackLink to={backUrl} label={backLabel} />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
              {data?.title ?? 'Vista previa'}
            </h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Vista previa del objeto de aprendizaje
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={detailUrl} className="lib-preview-btn lib-preview-btn--outline rounded-lg border border-dash-outline-variant px-4 py-2 text-sm font-medium">
            Detalle
          </Link>
          {canEdit && (
            <Link to={editUrl} className="lib-preview-btn lib-preview-btn--primary rounded-lg bg-dash-primary px-4 py-2 text-sm font-medium text-white">
              Editar
            </Link>
          )}
        </div>
      </header>

      {error && <Alert variant="danger" className="mb-4">{(error as Error).message}</Alert>}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader /></div>
      ) : data ? (
        <div className="grid gap-6 lg:grid-cols-12">
          <div className="lib-preview-viewer lg:col-span-9">
            <div className="lms-credentials-glass-panel min-h-[320px] rounded-xl border border-dash-outline-variant p-6 shadow-sm">
              <p className="text-sm text-dash-on-surface-variant">
                Vista previa del contenido — tipo <strong>{data.type}</strong>
              </p>
              {data.currentVersion?.content != null && (
                <pre className="mt-4 max-h-96 overflow-auto rounded-md bg-secondary-50 p-4 text-xs">
                  {JSON.stringify(data.currentVersion.content, null, 2)}
                </pre>
              )}
            </div>
          </div>
          <aside className="lib-preview-meta lg:col-span-3">
            <div className="lms-credentials-glass-panel rounded-xl border border-dash-outline-variant p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-dash-primary">Metadatos del Objeto</h2>
              <dl className="space-y-3 text-sm">
                <div><dt className="text-dash-on-surface-variant">Tipo</dt><dd className="font-medium">{data.type}</dd></div>
                <div><dt className="text-dash-on-surface-variant">Slug</dt><dd className="font-medium">{data.slug}</dd></div>
                <div><dt className="text-dash-on-surface-variant">Versión</dt><dd className="font-medium">{data.currentVersion?.versionNumber ?? 1}</dd></div>
                {data.status && <div><dt className="text-dash-on-surface-variant">Estado</dt><dd className="font-medium">{data.status}</dd></div>}
              </dl>
              <a href={`/library/${id}/preview`} target="_blank" rel="noreferrer" className="mt-4 block text-sm font-medium text-dash-secondary hover:underline">
                Abrir en nueva pestaña
              </a>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
